"use client";

import { Lightbulb, Send, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useState, useRef } from "react";

const ageOptions = ["Manje od 13", "13 do 15", "16 do 18", "19 do 25", "26 do 30", "više od 30"];
const genderOptions = ["Žensko", "Muško", "Drugo / Ne želim reći"];
const challengeOptions = [
  "Stres/mentalno zdravlje", "Nedostatak sadržaja za mlade", "Zapošljavanje/financijska neovisnost",
  "Nasilje/diskriminacija", "Odlazak mladih iz grada/države", "Problemi u obrazovanju", "Loša prometna povezanost/izoliranost",
];
const topicOptions = [
  "Osobni razvoj i mentalno zdravlje", "EU prilike (Erasmus+, ESC, ...)", "Ljudska prava / ravnopravnost",
  "Umjetnost i kreativno izražavanje", "Zeleni projekti vezani za okoliš i ekologiju",
  "Poduzetništvo i zapošljavanje", "Medijska pismenost", "Organizacija događaja",
  "Radionice, pisanje projekata i različite edukacije",
];

export function BaciIdejuForm() {
  const [selectedAge, setSelectedAge] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedChallenges, setSelectedChallenges] = useState<string[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [erasmusExperience, setErasmusExperience] = useState("");
  const [interested, setInterested] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const locationRef = useRef<HTMLInputElement>(null);
  const ideaRef = useRef<HTMLTextAreaElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const toggleChallenge = (c: string) => setSelectedChallenges((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c]);
  const toggleTopic = (t: string) => setSelectedTopics((p) => p.includes(t) ? p.filter((x) => x !== t) : [...p, t]);

  const chipCls = (active: boolean, theme: "amber" | "emerald" | "sky") =>
    `px-4 py-2 rounded-full text-sm font-medium transition-all ${active
      ? theme === "amber" ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white"
        : theme === "emerald" ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
        : "bg-gradient-to-r from-sky-500 to-blue-500 text-white"
      : "bg-muted text-muted-foreground hover:bg-muted/80"
    }`;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const res = await fetch("/api/submissions/baci-ideju", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          age: selectedAge,
          gender: selectedGender,
          location: locationRef.current?.value || null,
          challenges: selectedChallenges,
          topics: selectedTopics,
          erasmus_experience: erasmusExperience,
          interested,
          idea: ideaRef.current?.value || null,
          email: emailRef.current?.value || null,
        }),
      });
      if (!res.ok) throw new Error();
      setSubmitted(true);
    } catch {
      setError("Došlo je do greške. Pokušajte ponovo.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <section className="py-20 pb-32">
        <div className="max-w-xl mx-auto px-6 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-3" style={{ fontFamily: "var(--font-display)" }}>
            Hvala ti! 🎉
          </h2>
          <p className="text-muted-foreground text-lg">
            Tvoji odgovori su zaprimljeni. Bit ćemo u kontaktu!
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 pb-32">
      <div className="max-w-3xl mx-auto px-6">
        <form className="space-y-10" onSubmit={handleSubmit}>
          <div className="p-6 md:p-8 rounded-2xl bg-card border border-border">
            <Label className="text-lg font-semibold text-foreground mb-4 block">Koliko godina imaš?</Label>
            <div className="flex flex-wrap gap-3">
              {ageOptions.map((age) => (<button key={age} type="button" onClick={() => setSelectedAge(age)} className={chipCls(selectedAge === age, "amber")}>{age}</button>))}
            </div>
          </div>

          <div className="p-6 md:p-8 rounded-2xl bg-card border border-border">
            <Label className="text-lg font-semibold text-foreground mb-4 block">Spol</Label>
            <div className="flex flex-wrap gap-3">
              {genderOptions.map((g) => (<button key={g} type="button" onClick={() => setSelectedGender(g)} className={chipCls(selectedGender === g, "amber")}>{g}</button>))}
            </div>
          </div>

          <div className="p-6 md:p-8 rounded-2xl bg-card border border-border">
            <Label className="text-lg font-semibold text-foreground mb-4 block">Mjesto prebivališta (općina, grad, država)</Label>
            <Input ref={locationRef} placeholder="npr. Metković, Hrvatska" className="rounded-xl bg-background" />
          </div>

          <div className="p-6 md:p-8 rounded-2xl bg-card border border-border">
            <Label className="text-lg font-semibold text-foreground mb-4 block">Koji su ti najveći izazovi danas kao mladoj osobi?</Label>
            <div className="flex flex-wrap gap-3">
              {challengeOptions.map((c) => (<button key={c} type="button" onClick={() => toggleChallenge(c)} className={chipCls(selectedChallenges.includes(c), "emerald")}>{c}</button>))}
            </div>
          </div>

          <div className="p-6 md:p-8 rounded-2xl bg-card border border-border">
            <Label className="text-lg font-semibold text-foreground mb-4 block">Koje teme te zanimaju?</Label>
            <div className="flex flex-wrap gap-3">
              {topicOptions.map((t) => (<button key={t} type="button" onClick={() => toggleTopic(t)} className={chipCls(selectedTopics.includes(t), "sky")}>{t}</button>))}
            </div>
          </div>

          <div className="p-6 md:p-8 rounded-2xl bg-card border border-border">
            <Label className="text-lg font-semibold text-foreground mb-4 block">Jesi li do sada sudjelovao/la u nekom Erasmus+ ili sličnom EU projektu?</Label>
            <div className="flex flex-wrap gap-3">
              {["Da", "Ne", "Nisam siguran/na što je to"].map((o) => (<button key={o} type="button" onClick={() => setErasmusExperience(o)} className={chipCls(erasmusExperience === o, "amber")}>{o}</button>))}
            </div>
          </div>

          <div className="p-6 md:p-8 rounded-2xl bg-card border border-border">
            <Label className="text-lg font-semibold text-foreground mb-4 block">Jesi li zainteresiran/na za sudjelovanje u besplatnim aktivnostima za mlade?</Label>
            <div className="flex flex-wrap gap-3">
              {["Da", "Ne", "Možda, ovisi o temi i vremenu"].map((o) => (<button key={o} type="button" onClick={() => setInterested(o)} className={chipCls(interested === o, "amber")}>{o}</button>))}
            </div>
          </div>

          <div className="p-6 md:p-8 rounded-2xl bg-card border border-border">
            <Label className="text-lg font-semibold text-foreground mb-4 block">Imaš li ideju što bi moglo poboljšati život mladih u našem gradu?</Label>
            <Textarea ref={ideaRef} placeholder="Podjeli svoju ideju s nama..." className="rounded-xl bg-background min-h-[120px]" />
          </div>

          <div className="p-6 md:p-8 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100">
            <Label className="text-lg font-semibold text-foreground mb-2 block">Ostavi nam e-mail adresu</Label>
            <p className="text-sm text-muted-foreground mb-4">Da ti pošaljemo informacije o budućim projektima i aktivnostima koje udruga provodi.</p>
            <Input ref={emailRef} type="email" placeholder="tvoj@email.com" className="rounded-xl bg-white" />
          </div>

          {error && <p className="text-red-500 text-sm text-center">{error}</p>}

          <Button type="submit" size="lg" disabled={submitting} className="w-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-6 text-lg disabled:opacity-60">
            <Send className="w-5 h-5 mr-2" />
            {submitting ? "Šalje se..." : "Pošalji"}
          </Button>
        </form>
      </div>
    </section>
  );
}
